import { Shirt, UserRound, Image as ImageIcon, Table2, ChevronDown, X, Link2, Plus, CircleSlash, CircleCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Small action button used inside BulkActionBar. Kept private to this file
 * because its styling is tightly coupled to the dark floating bar.
 */
const Action = ({ icon: Icon, label, onClick, testId, chevron }) => (
    <button
        type="button"
        onClick={onClick}
        className="px-3.5 py-2 rounded-lg flex items-center gap-1.5 text-xs font-medium hover:bg-background/10 active:bg-background/15 transition-colors"
        data-testid={testId}
    >
        <Icon className="h-3.5 w-3.5 opacity-80" strokeWidth={1.75} />
        <span>{label}</span>
        {chevron && <ChevronDown className="h-3 w-3 opacity-60 -mr-0.5" strokeWidth={2} />}
    </button>
);

/**
 * MenuItem for the "Beden tablosu ▾" sub-popover. Uses the app's default
 * (light) surface — the popover is anchored outside the dark bar so keeps
 * the standard menu look for consistency with everything else.
 */
const MenuItem = ({ icon: Icon, label, description, onClick, testId, tone = "default" }) => (
    <button
        type="button"
        onClick={onClick}
        className={`w-full text-left px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors flex items-start gap-3 ${tone === "danger" ? "text-destructive hover:bg-destructive/5" : ""}`}
        data-testid={testId}
    >
        <div className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${tone === "danger" ? "bg-destructive/10" : "bg-muted"}`}>
            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
            <div className="text-xs font-medium">{label}</div>
            {description && <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-relaxed">{description}</div>}
        </div>
    </button>
);

/**
 * BulkActionBar — floating dark pill anchored to the bottom of the Products
 * page whenever the merchant has 2+ rows selected. Deliberate design goals:
 *
 *   • Attention-grabbing but not intrusive.  Sits ~24px above the bottom edge,
 *     centred, with a strong shadow so it looks like it's floating over the
 *     rest of the page.  Slides + fades in on mount for a subtle "hey, here I
 *     am" cue without any modal-style overlay.
 *   • Fixed layout, no reflow.  All buttons visible at once except the Beden
 *     Tablosu sub-menu (grouped by natural association: linking / creating /
 *     muaf-ing all live in the "tablo" mental model).
 *   • Dark on light + light on dark.  Uses `bg-foreground text-background` so
 *     the bar always contrasts strongly against the page regardless of theme.
 *   • Selection state visible up front.  Left compartment shows count with a
 *     pulsing brand dot; "Hepsini Seç" appears when total > selected so the
 *     merchant can escalate quickly.
 */
export const BulkActionBar = ({
    selectedCount, totalCount, onSelectAll, onClear,
    onKalip, onModel, onGorsel,
    onLinkTable, onCreateTable,
    anyExempt, allExempt,
    onMarkExempt, onUnmarkExempt,
}) => (
    <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-in slide-in-from-bottom-6 fade-in duration-300"
        data-testid="bulk-action-bar"
    >
        <div className="rounded-2xl bg-foreground text-background border border-background/10 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35)] flex items-stretch gap-1 p-1.5 backdrop-blur-md">
            {/* Selection info compartment */}
            <div className="pl-3 pr-2 flex items-center gap-2.5">
                <span className="relative flex h-2 w-2" aria-hidden>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand opacity-60" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-brand" />
                </span>
                <span className="text-xs font-semibold" data-testid="bulk-count">
                    {selectedCount} ürün seçili
                </span>
                {selectedCount < totalCount && (
                    <button
                        onClick={onSelectAll}
                        className="text-[11px] underline underline-offset-2 opacity-60 hover:opacity-100 transition-opacity"
                        data-testid="bulk-select-all"
                    >
                        {totalCount}&apos;i seç
                    </button>
                )}
            </div>

            <div className="w-px bg-background/15 my-1.5" />

            {/* Action buttons */}
            <div className="flex items-center gap-0.5">
                <Action icon={Shirt} label="Kalıp" onClick={onKalip} testId="bulk-kalip" />
                <Action icon={UserRound} label="Model" onClick={onModel} testId="bulk-model" />
                <Action icon={ImageIcon} label="Görsel" onClick={onGorsel} testId="bulk-gorsel" />

                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="px-3.5 py-2 rounded-lg flex items-center gap-1.5 text-xs font-medium hover:bg-background/10 active:bg-background/15 transition-colors data-[state=open]:bg-background/15"
                            data-testid="bulk-table"
                        >
                            <Table2 className="h-3.5 w-3.5 opacity-80" strokeWidth={1.75} />
                            <span>Beden Tablosu</span>
                            <ChevronDown className="h-3 w-3 opacity-60 -mr-0.5" strokeWidth={2} />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="center"
                        side="top"
                        sideOffset={12}
                        className="w-72 p-1.5"
                        data-testid="bulk-table-menu"
                    >
                        <MenuItem
                            icon={Link2}
                            label="Mevcut tabloya bağla"
                            description="Var olan bir tablo seç, seçili ürünlere aynı tabloyu bağla."
                            onClick={onLinkTable}
                            testId="bulk-link-existing"
                        />
                        <MenuItem
                            icon={Plus}
                            label="Yeni tablo oluştur"
                            description="Bir tablo hazırla, seçili ürünlerin hepsini otomatik bağla."
                            onClick={onCreateTable}
                            testId="bulk-create-new"
                        />
                        {!allExempt && (
                            <>
                                <div className="h-px bg-border my-1" />
                                <MenuItem
                                    icon={CircleSlash}
                                    label="Muaf olarak işaretle"
                                    description="Aksesuar / kozmetik gibi ürünleri &ldquo;Tablosu yok&rdquo; listesinden çıkar."
                                    onClick={onMarkExempt}
                                    testId="bulk-mark-exempt"
                                />
                            </>
                        )}
                        {anyExempt && (
                            <>
                                {allExempt && <div className="h-px bg-border my-1" />}
                                <MenuItem
                                    icon={CircleCheck}
                                    label="Muaf işaretini kaldır"
                                    description="Bu ürünler tekrar &ldquo;Tablosu yok&rdquo; listesine dönecek."
                                    onClick={onUnmarkExempt}
                                    testId="bulk-unmark-exempt"
                                />
                            </>
                        )}
                    </PopoverContent>
                </Popover>
            </div>

            <div className="w-px bg-background/15 my-1.5" />

            {/* Clear button */}
            <button
                type="button"
                onClick={onClear}
                title="Seçimi temizle"
                className="px-3 flex items-center gap-1.5 text-xs opacity-60 hover:opacity-100 transition-opacity"
                data-testid="bulk-clear"
                aria-label="Seçimi temizle"
            >
                <X className="h-3.5 w-3.5" />
                <span>Temizle</span>
            </button>
        </div>
    </div>
);
